import React, { useEffect, useState, useCallback } from 'react';
import { useStore } from '../context/StoreContext';
import { ProductCard } from '../components/ProductCard';
import { Pagination } from '../components/Pagination';
import { TRANSLATIONS } from '../constants';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { SearchX } from 'lucide-react';
import { CategoryPageSkeleton } from '../components/Shimmer';
import { updateMetaTags, injectStructuredData, generateBreadcrumbStructuredData } from '../utils/seo';
import { SEO_CONFIG, getFullUrl } from '../config/seo.config';
import { productsApi } from '../services/apiService';
import { Product } from '../types';

const PRODUCTS_PER_PAGE = 60;

export const CategoryPage = () => {
  const { categories, language, searchQuery } = useStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { slugOrId } = useParams<{ slugOrId?: string }>();
  
  const [pagedProducts, setPagedProducts] = useState<Product[]>([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const selectedCategoryId = searchParams.get('cat') 
    || (slugOrId 
        ? (categories.find(c => c.slug === slugOrId || c.id === slugOrId)?.id || 'all')
        : 'all');

  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1'));

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page: currentPage, limit: PRODUCTS_PER_PAGE };
      if (selectedCategoryId !== 'all') params.category = selectedCategoryId;
      if (searchQuery?.trim()) params.search = searchQuery.trim();

      const response = await productsApi.getAll(params);
      if (response.success && response.data) {
        const transformedProducts = (response.data as any[]).map((p: any) => ({
          id: p.id,
          name: p.name,
          slug: p.slug,
          price: typeof p.price === 'string' ? parseFloat(p.price) : p.price,
          categoryId: p.category_id || p.categoryId,
          description: p.description,
          image: p.image,
          stock: p.stock,
          discount: p.discount || 0,
          featured: p.featured || false,
          minQuantity: p.min_quantity || p.minQuantity || 6,
        }));
        setPagedProducts(transformedProducts);
        setTotalProducts((response as any).total || transformedProducts.length);
        setTotalPages((response as any).totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategoryId, searchQuery]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    const category = categories.find(c => c.id === selectedCategoryId);
    const categoryName = category ? category.name[language] : (language === 'en' ? 'All Categories' : 'Wszystkie Kategorie');
    
    const categoryPath = category?.slug 
      ? `/category/${category.slug}` 
      : selectedCategoryId !== 'all' 
        ? `?cat=${selectedCategoryId}` 
        : '';
    
    updateMetaTags({
      title: `${categoryName} - ${SEO_CONFIG.storeName}`,
      description: `Browse our ${categoryName.toLowerCase()} collection. Discover quality products at great prices.`,
      keywords: `${categoryName}, online shopping, ${SEO_CONFIG.storeName.toLowerCase()}, e-commerce`,
      canonical: getFullUrl(`/categories${categoryPath}`),
      ogType: 'website',
    });

    const breadcrumbs = generateBreadcrumbStructuredData([
      { name: 'Home', url: getFullUrl('/') },
      { name: categoryName, url: getFullUrl(`/categories${categoryPath}`) },
    ]);

    injectStructuredData(breadcrumbs);
  }, [selectedCategoryId, categories, language, slugOrId]);

  const handlePageChange = (page: number) => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(page));
    setSearchParams(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategoryChange = (params: Record<string, string>) => {
    if (Object.keys(params).length === 0 && slugOrId) {
      navigate('/categories');
    } else {
      const next = new URLSearchParams(params);
      next.delete('page');
      setSearchParams(next);
    }
  };

  const startItem = (currentPage - 1) * PRODUCTS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * PRODUCTS_PER_PAGE, totalProducts);

  return (
    <main>
      <h1 className="text-3xl font-bold mb-8 text-gray-900">{TRANSLATIONS.categories[language]}</h1>

      <nav className="flex flex-wrap gap-2 sm:gap-4 mb-8" aria-label="Category filters" role="tablist">
        <button
          onClick={() => handleCategoryChange({})}
          className={`px-3 py-1 text-sm sm:px-6 sm:py-2 sm:text-base rounded-full font-medium transition-colors ${
            selectedCategoryId === 'all' 
              ? 'bg-emerald-600 text-white' 
              : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
          }`}
          role="tab"
          aria-selected={selectedCategoryId === 'all'}
        >
          {language === 'en' ? 'All' : 'Wszystkie'}
        </button>
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => handleCategoryChange({ cat: cat.id })}
            className={`px-3 py-1 text-sm sm:px-6 sm:py-2 sm:text-base rounded-full font-medium transition-colors ${
              selectedCategoryId === cat.id 
                ? 'bg-emerald-600 text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
            role="tab"
            aria-selected={selectedCategoryId === cat.id}
          >
            {cat.name[language]}
          </button>
        ))}
      </nav>

      {loading && <CategoryPageSkeleton count={12} />}

      {!loading && pagedProducts.length > 0 && (
        <>
          <p className="text-sm text-gray-500 mb-4">
            {language === 'en'
              ? `Showing ${startItem}–${endItem} of ${totalProducts} products`
              : `Wyświetlanie ${startItem}–${endItem} z ${totalProducts} produktów`}
          </p>

          <section className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6" role="list" aria-label="Product list">
            {pagedProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </section>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}

      {!loading && pagedProducts.length === 0 && (
        <div className="col-span-full py-20 text-center text-gray-400 flex flex-col items-center" role="status">
          <SearchX className="w-12 h-12 mb-3 text-gray-200" aria-hidden="true" />
          <p>{language === 'en' ? 'No products found.' : 'Nie znaleziono produktów.'}</p>
        </div>
      )}
    </main>
  );
};